import React, { useState, useEffect } from 'react';
import { X, Play, Edit } from 'lucide-react';
import { useSoundworlds } from '../../hooks/useSoundworlds';

interface SoundworldCard {
    id: string;
    name: string;
    artist: string;
    description: string;
    image: string;
    status: 'ready' | 'training' | 'error';
}

interface ChooseSoundworldModalSimpleProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (soundworld: string) => void;
}

export const ChooseSoundworldModalSimple: React.FC<ChooseSoundworldModalSimpleProps> = ({
    isOpen,
    onClose,
    onSelect,
}) => {
    const { soundworlds, isLoading } = useSoundworlds();
    const [selectedSoundworld, setSelectedSoundworld] = useState<string | null>(null);
    const [playingPreview, setPlayingPreview] = useState<string | null>(null);
    const [audioRef, setAudioRef] = useState<HTMLAudioElement | null>(null);

    useEffect(() => {
        return () => {
            if (audioRef) {
                audioRef.pause();
                audioRef.src = '';
                setAudioRef(null);
                setPlayingPreview(null);
            }
        };
    }, [audioRef]);

    useEffect(() => {
        if (!isOpen && audioRef) {
            audioRef.pause();
            audioRef.src = '';
            setAudioRef(null);
            setPlayingPreview(null);
        }
    }, [isOpen, audioRef]);

    const handlePreview = (soundworldId: string) => {
        const soundworld = soundworlds.find(sw => sw.id === soundworldId);

        if (!soundworld?.sample) {
            return;
        }

        if (playingPreview === soundworldId && audioRef) {
            audioRef.pause();
            setPlayingPreview(null);
            setAudioRef(null);
        } else {
            if (audioRef) {
                audioRef.pause();
                setAudioRef(null);
            }

            const audio = new Audio(soundworld.sample);

            audio.addEventListener('ended', () => {
                setPlayingPreview(null);
                setAudioRef(null);
            });

            audio.addEventListener('error', (e) => {
                console.error('Audio error:', e);
                setPlayingPreview(null);
                setAudioRef(null);
            });

            audio.play().then(() => {
                setPlayingPreview(soundworldId);
                setAudioRef(audio);
            }).catch((error) => {
                console.error('Audio play failed:', error);
                setPlayingPreview(null);
                setAudioRef(null);
            });
        }
    };

    const handleUseEdit = (soundworldId: string) => {
        setSelectedSoundworld(soundworldId);
        onSelect(soundworldId);
        onClose();
    };

    if (!isOpen) return null;

    const soundworldCards: SoundworldCard[] = soundworlds.map(sw => ({
        id: sw.id,
        name: sw.name,
        artist: sw.artist || 'Unknown Artist',
        description: sw.description || '',
        image: sw.image || `https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=300&fit=crop`,
        status: sw.status || 'ready'
    }));

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={ onClose }>
            <div className="bg-card rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto" onClick={ (e) => e.stopPropagation() }>
                {/* Header */ }
                <div className="flex items-center justify-between p-6 border-b border-border">
                    <h2 className="text-2xl font-bold text-card-foreground">Choose Soundworld</h2>
                    <button
                        onClick={ onClose }
                        className="text-muted-foreground hover:text-card-foreground cursor-pointer"
                    >
                        <X size={ 24 } />
                    </button>
                </div>

                {/* Soundworlds Section */ }
                <div className="p-6">
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-2 text-card-foreground">Your soundworlds</h3>
                        <p className="text-sm text-muted-foreground">
                            Access your soundworlds, generate, edit your sounds and train your soundworld
                        </p>
                    </div>

                    { isLoading ? (
                        <div className="text-center py-8">
                            <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin mx-auto"></div>
                            <p className="text-muted-foreground mt-2">Loading soundworlds...</p>
                        </div>
                    ) : (
                        <>
                            {/* Soundworld Grid */ }
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                { soundworldCards.map((soundworld) => (
                                    <div
                                        key={ soundworld.id }
                                        className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-md transition-shadow flex flex-col h-full"
                                    >
                                        {/* Image */ }
                                        <div className="relative">
                                            <img
                                                src={ soundworld.image }
                                                alt={ soundworld.name }
                                                className="w-full h-32 object-cover"
                                            />
                                            { soundworld.status === 'ready' && (
                                                <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                                                    Ready
                                                </div>
                                            ) }
                                        </div>

                                        {/* Content */ }
                                        <div className="p-4 flex flex-col flex-1">
                                            <h4 className="font-semibold text-sm mb-1 text-card-foreground">{ soundworld.name }</h4>
                                            <p className="text-xs text-muted-foreground mb-2">{ soundworld.artist }</p>
                                            <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
                                                { soundworld.description }
                                            </p>

                                            <div className="flex gap-2 mt-auto">
                                                <button
                                                    onClick={ () => handlePreview(soundworld.id) }
                                                    className={ `flex-1 text-xs px-2 py-1 border rounded flex items-center justify-center gap-1 cursor-pointer ${playingPreview === soundworld.id
                                                        ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
                                                        : 'border-border bg-transparent text-card-foreground hover:bg-accent hover:text-accent-foreground'
                                                        }` }
                                                >
                                                    { playingPreview === soundworld.id ? (
                                                        <>
                                                            <div className="w-3 h-3 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                                                            Stop
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Play size={ 12 } />
                                                            Preview
                                                        </>
                                                    ) }
                                                </button>
                                                <button
                                                    onClick={ () => handleUseEdit(soundworld.id) }
                                                    className="flex-1 text-xs px-2 py-1 rounded flex items-center justify-center gap-1 btn-primary"
                                                >
                                                    <Edit size={ 12 } />
                                                    Use/Edit
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )) }
                            </div>

                            {/* Featured Public Soundworlds */ }
                            { soundworldCards.length > 4 && (
                                <div className="mt-8">
                                    <h3 className="text-lg font-semibold mb-4 text-card-foreground">Featured public soundworlds</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                        { soundworldCards.slice(4).map((soundworld) => (
                                            <div
                                                key={ soundworld.id }
                                                className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-md transition-shadow flex flex-col h-full"
                                            >
                                                <div className="relative">
                                                    <img
                                                        src={ soundworld.image }
                                                        alt={ soundworld.name }
                                                        className="w-full h-32 object-cover"
                                                    />
                                                    { soundworld.status === 'ready' && (
                                                        <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                                                            Ready
                                                        </div>
                                                    ) }
                                                </div>
                                                <div className="p-4 flex flex-col flex-1">
                                                    <h4 className="font-semibold text-sm mb-1 text-card-foreground">{ soundworld.name }</h4>
                                                    <p className="text-xs text-muted-foreground mb-2">{ soundworld.artist }</p>
                                                    <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
                                                        { soundworld.description }
                                                    </p>
                                                    <div className="flex gap-2 mt-auto">
                                                        <button
                                                            onClick={ () => handlePreview(soundworld.id) }
                                                            className={ `flex-1 text-xs px-2 py-1 border rounded flex items-center justify-center gap-1 cursor-pointer ${playingPreview === soundworld.id
                                                                ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
                                                                : 'border-border bg-transparent text-card-foreground hover:bg-accent hover:text-accent-foreground'
                                                                }` }
                                                        >
                                                            { playingPreview === soundworld.id ? (
                                                                <>
                                                                    <div className="w-3 h-3 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                                                                    Stop
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Play size={ 12 } />
                                                                    Preview
                                                                </>
                                                            ) }
                                                        </button>
                                                        <button
                                                            onClick={ () => handleUseEdit(soundworld.id) }
                                                            className="flex-1 text-xs px-2 py-1 rounded flex items-center justify-center gap-1 btn-primary"
                                                        >
                                                            <Edit size={ 12 } />
                                                            Use/Edit
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )) }
                                    </div>
                                </div>
                            ) }
                        </>
                    ) }
                </div>
            </div>
        </div>
    );
}; 